import { AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { WarningTriangleFilledIcon } from '../icons';
import { pushNotificationService } from '../services/push-notifications';

type BrowserType = 'chrome' | 'firefox' | 'safari' | 'edge' | 'unknown';

function detectBrowser(): BrowserType {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome') && !ua.includes('edg/')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  return 'unknown';
}

const BROWSER_HELP_LINKS: Record<BrowserType, { name: string; url: string }> = {
  chrome: {
    name: 'Chrome',
    url: 'https://support.google.com/chrome/answer/3220216',
  },
  firefox: {
    name: 'Firefox',
    url: 'https://support.mozilla.org/en-US/kb/push-notifications-firefox',
  },
  safari: {
    name: 'Safari',
    url: 'https://support.apple.com/guide/safari/customize-website-notifications-sfri40734/mac',
  },
  edge: {
    name: 'Edge',
    url: 'https://support.microsoft.com/en-us/microsoft-edge/manage-website-notifications-in-microsoft-edge-0c555609-5bf2-479d-a59d-fb30a0b80b2b',
  },
  unknown: {
    name: 'your browser',
    url: '',
  },
};

export function NotificationSettings() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browser] = useState<BrowserType>(() => detectBrowser());

  useEffect(() => {
    checkNotificationSupport();
  }, []);

  const checkNotificationSupport = async () => {
    const supported = pushNotificationService.isSupported();
    setIsSupported(supported);

    if (supported) {
      const permissionStatus = pushNotificationService.getPermissionStatus();
      setPermission(permissionStatus);

      const subscribed = await pushNotificationService.isSubscribed();
      setIsSubscribed(subscribed);
    }
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await pushNotificationService.requestPermissionAndSubscribe();
      if (success) {
        setPermission('granted');
        setIsSubscribed(true);
      } else {
        setPermission(pushNotificationService.getPermissionStatus());
      }
    } catch (err) {
      console.error('Failed to enable notifications:', err);
      setError('Failed to enable notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await pushNotificationService.unsubscribe();
      if (success) {
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error('Failed to disable notifications:', err);
      setError('Failed to disable notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await pushNotificationService.showTestNotification();
    } catch (error) {
      console.error('Failed to show test notification:', error);
    }
  };

  if (!isSupported) {
    return (
      <div
        className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4"
        data-testid="notifications-not-supported"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <WarningTriangleFilledIcon className="text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-200">Notifications Not Supported</h3>
            <p className="mt-2 text-sm text-yellow-300">
              Your browser does not support push notifications. Please use a modern browser like
              Chrome, Firefox, or Safari.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const browserInfo = BROWSER_HELP_LINKS[browser];

  return (
    <div
      className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg p-6"
      data-testid="notification-settings"
    >
      <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
        Notification Settings
      </h2>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">
            Push Notifications
          </h3>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
            Get notified when you receive new messages
          </p>

          <div className="mt-4 space-y-2">
            <div className="flex items-center">
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Status:
              </span>
              <span className="ml-2">
                {permission === 'granted' && isSubscribed && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 border border-green-300 dark:border-green-600"
                    data-testid="notification-status-enabled"
                  >
                    Enabled
                  </span>
                )}
                {permission === 'granted' && !isSubscribed && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100 border border-yellow-300 dark:border-yellow-600"
                    data-testid="notification-status-permission-granted"
                  >
                    Permission Granted
                  </span>
                )}
                {permission === 'denied' && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 border border-red-300 dark:border-red-600"
                    data-testid="notification-status-blocked"
                  >
                    Blocked
                  </span>
                )}
                {permission === 'default' && (
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-neutral-100 text-neutral-800 dark:bg-neutral-600 dark:text-neutral-100 border border-neutral-300 dark:border-neutral-500"
                    data-testid="notification-status-not-enabled"
                  >
                    Not Enabled
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3" data-testid="notification-actions">
        {!isSubscribed && permission !== 'denied' && (
          <button
            onClick={handleEnableNotifications}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            data-testid="enable-notifications-button"
          >
            {isLoading ? 'Enabling...' : 'Enable Notifications'}
          </button>
        )}

        {isSubscribed && (
          <>
            <button
              onClick={handleDisableNotifications}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-neutral-400 dark:border-neutral-600 text-sm font-medium rounded-md text-neutral-900 dark:text-neutral-300 bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              data-testid="disable-notifications-button"
            >
              {isLoading ? 'Disabling...' : 'Disable Notifications'}
            </button>
            <button
              onClick={handleTestNotification}
              className="inline-flex items-center px-4 py-2 border border-neutral-400 dark:border-neutral-600 text-sm font-medium rounded-md text-neutral-900 dark:text-neutral-300 bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              data-testid="test-notification-button"
            >
              Test Notification
            </button>
          </>
        )}
      </div>

      {error && (
        <div
          className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200"
          data-testid="notification-error"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {permission === 'denied' && (
        <div
          className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg"
          data-testid="notification-blocked-message"
        >
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            Notifications are blocked
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            You&apos;ve previously denied notification permissions. To enable notifications,
            you&apos;ll need to update your browser settings.
            {browserInfo.url && (
              <>
                {' '}
                <a
                  href={browserInfo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 underline underline-offset-2"
                  data-testid="notification-help-link"
                >
                  Learn more →
                </a>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
